import { useEffect, useState } from 'react';

import { prefersReducedMotion } from '../../hooks/usePrefersReducedMotion';
import { siteConfig } from '../../config/site';

const SEEN_KEY = 'portfolio:intro-seen';
const DURATION = 1400;

function alreadySeen() {
  try {
    return window.sessionStorage.getItem(SEEN_KEY) === '1';
  } catch {
    return false;
  }
}

function markSeen() {
  try {
    window.sessionStorage.setItem(SEEN_KEY, '1');
  } catch {
    /* storage unavailable: intro simply shows again next time */
  }
}

/** Short intro counter + curtain, shown once per browser session. */
export default function Preloader() {
  const [skip] = useState(() => prefersReducedMotion() || alreadySeen());
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState(skip ? 'done' : 'counting');

  useEffect(() => {
    if (skip) {
      document.body.classList.add('is-ready');
      return undefined;
    }
    document.body.classList.add('is-locked');
    let frame;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / DURATION);
      setProgress(Math.round((1 - Math.pow(1 - t, 3)) * 100));
      if (t < 1) {
        frame = requestAnimationFrame(tick);
      } else {
        setPhase('leaving');
      }
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      document.body.classList.remove('is-locked');
    };
  }, [skip]);

  useEffect(() => {
    if (phase !== 'leaving') return undefined;
    markSeen();
    document.body.classList.remove('is-locked');
    document.body.classList.add('is-ready');
    const id = setTimeout(() => setPhase('done'), 900);
    return () => clearTimeout(id);
  }, [phase]);

  if (phase === 'done') return null;

  const [first, ...rest] = siteConfig.displayName.split(' ');

  return (
    <div className={`preloader${phase === 'leaving' ? ' preloader--leaving' : ''}`} aria-hidden="true">
      <div className="preloader__inner">
        <p className="preloader__name">
          <span>{first}</span> <span className="gradient-text">{rest.join(' ')}</span>
        </p>
        <div className="preloader__bar">
          <span style={{ transform: `scaleX(${progress / 100})` }} />
        </div>
        <p className="preloader__count">{String(progress).padStart(3, '0')}</p>
      </div>
    </div>
  );
}
